import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import { SVGWrapper } from '../../components'
import { IconClose } from '../../assets/icons'
import { ReactComponent as PlanIcon } from '../../assets/icons/IpmIconPlan.svg'
import ChatHistoryItem from './ChatHistoryItem'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  /* Design: a white scrim, not a dark dim. */
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: flex-end;
`

const Sheet = styled.div`
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 1px 30px 7px rgba(122, 142, 150, 0.25);
  padding: 20px 20px max(20px, env(safe-area-inset-bottom));
  box-sizing: border-box;
`

// Centered "Recents" title with the close control pinned top-right; no divider.
const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
`

const Title = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
`

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  width: 45px;
  height: 45px;
  cursor: pointer;
  & svg {
    width: 25px;
    height: 25px;
  }
  & svg .icon-color {
    fill: ${p => p.theme.lightestGray ?? '#7a8e96'};
    stroke: ${p => p.theme.lightestGray ?? '#7a8e96'};
  }
`

// Full-width outlined pill matching the desktop "New chat" control.
const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 20px;
  padding: 5px 20px;
  border-radius: 76px;
  border: 1px solid ${p => p.theme.lightestGray ?? '#7a8e96'};
  background: transparent;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  font-weight: normal;
  cursor: pointer;
  & svg {
    display: block;
    width: 25px;
    height: 25px;
  }
  /* Retint the pencil glyph's inner group to match the label. */
  & svg g g {
    fill: currentColor;
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

const List = styled.div`
  margin-top: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

// Bottom action sheet listing recent chats (mobile). Dismiss by tapping the X,
// tapping outside, or starting a new chat.
const MobileChatSheet = ({ store, open, onClose, inputRef }) => {
  if (!open) return null

  const chats = store.sortedChatMetadatas
  const activeId = store.activeChat?.id ?? null
  // Locked while a response streams so the user can't abort the in-flight
  // stream by switching / creating / deleting a chat (see ChatHistorySidebar).
  const streaming = store.responseLoading

  /*
   * Raise the keyboard for the chat we're switching to. This has to happen here,
   * synchronously inside the tap: mobile browsers only open the keyboard when
   * focus() runs during a real user gesture, and ChatInput's own focus effect
   * fires on the chat-id change — i.e. after the chat fetch resolves, long past
   * the gesture. Focusing first also means the tap never parks focus on the
   * tapped row, so an already-open keyboard doesn't flicker shut and back.
   */
  const focusInput = () => inputRef?.current?.focus()

  const handleSelect = id => {
    if (streaming) return
    focusInput()
    store.openChat(id)
    onClose()
  }

  const handleNewChat = () => {
    if (streaming) return
    focusInput()
    store.startNewChat()
    onClose()
  }

  const handleDelete = async id => {
    if (streaming) return
    await store.removeChat(id)
  }

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={e => e.stopPropagation()}>
        <Header>
          <Title>Recents</Title>
          <CloseButton type='button' onClick={onClose} aria-label='Close'>
            <SVGWrapper svg={IconClose} size='medium' />
          </CloseButton>
        </Header>
        <NewChatButton type='button' onClick={handleNewChat} disabled={streaming}>
          <PlanIcon /> New chat
        </NewChatButton>
        <List>
          {chats.map(meta => (
            <ChatHistoryItem
              key={meta.id}
              metadata={meta}
              mobile
              selected={meta.id === activeId}
              disabled={streaming}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />
          ))}
        </List>
      </Sheet>
    </Overlay>
  )
}

MobileChatSheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** The chat textarea, focused inside the tap so the keyboard stays up. */
  inputRef: PropTypes.shape({ current: PropTypes.any }),
}

export default inject('store')(observer(MobileChatSheet))
